/* eslint-disable @typescript-eslint/no-magic-numbers */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2,
  Signal,
  ViewChild,
  WritableSignal,
  signal,
} from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  map,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { CommonModule } from '@angular/common';

import { RouterModule } from '@angular/router';
import { TranslocoModule } from '@ngneat/transloco';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { AkitaCheckoutQuery } from '@app/akita/api/checkout/state/checkout.query';
import { AkitaCheckoutService } from '@app/akita/api/checkout/state/checkout.service';
import { getCurrentHost } from '@app/shared/utils/url.utils';
import { SentryUtil } from '@app/shared/utils/sentry.util';
import { PaymentModel } from '@app/akita/api/checkout/models/payment.model';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import {
  logRequestOTP,
  logSentOTP,
  logStcErrorPayment,
} from '@app/core/services/google-analytics.lazy';
// eslint-disable-next-line max-len
import { ArrowLeft1IconComponent } from '@app/shared/components/icons/generated/arrow-left-1/arrow-left-1.component';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { Product } from '@app/akita/api/products/models/product.model';
import { PriceModel } from '@app/shared/models/api/price.model';
import { AmountPipe } from '@app/shared/pipes/amount.pipe';
import { LottieAnimationComponent } from '@app/components/lottie-animation/lottie-animation.component';
import { LoadingComponent } from '@app/shared/components/loading/loading.component';
import { A11yModule } from '@angular/cdk/a11y';
import { PhoneMetadata } from '@app/components/inputs/phone-input/popsy-phone-input.component';

import { toSignal } from '@angular/core/rxjs-interop';
import { PhoneInfo } from '@app/akita/api/addresses/models/phone.model';
import { StcPhoneInputComponent } from '../stc-phone-input/stc-phone-input.component';
import { parseApiError } from '@app/shared/models/api/api-error.model';

// 5 seconds
export const PAYMENT_STATUS_FETCH_INTERVAL = 5000;
export const SHOW_STC_PAY_LOGOS = true;

@Component({
  selector: 'app-otp-verification',
  templateUrl: './otp-verification.template.html',
  styleUrls: ['./otp-verification.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
    LoadingComponent,
    LottieAnimationComponent,
    ArrowLeft1IconComponent,
    StcPhoneInputComponent,
    A11yModule,
    AmountPipe,
  ],
})
export class OtpVerificationComponent implements OnDestroy, AfterViewInit {
  @Input() public products?: Array<Product> | null;
  @Input() public totalAmount?: PriceModel | null;

  @Output() public readonly payment: EventEmitter<boolean | null>;

  @ViewChild('otpInput') public optInputElement!: ElementRef<HTMLInputElement>;

  private readonly focusedSubject: BehaviorSubject<boolean>;
  public readonly focused$: Observable<boolean>;

  public lessThan$: Observable<ScreenSizeInfoState>;

  public paymentId: string | null = null;

  public sessionPhoneNumber: Signal<PhoneInfo | null>;
  public modifiedPhoneNumber: WritableSignal<PhoneMetadata | null> = signal(null);

  public phoneNumberConfirmed: WritableSignal<boolean> = signal(false);

  private readonly processingPaymentSubject: BehaviorSubject<boolean>;
  public readonly processingPayment$: Observable<boolean>;

  private readonly requestingOtpAgainSubject: BehaviorSubject<boolean>;
  public readonly requestingOtpAgain$: Observable<boolean>;

  public counter$: Observable<void>;
  private readonly requestingAgainCounterdownSubject: BehaviorSubject<number>;
  public readonly requestingAgainCountdown$: Observable<number>;

  public readonly optFormControl: UntypedFormControl;

  private readonly subscriptions: Subscription;
  private readonly counterSubscription: Subscription;
  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly zone: NgZone,
    private readonly renderer2: Renderer2,
    private readonly akitaCheckoutQuery: AkitaCheckoutQuery,
    private readonly akitaCheckoutService: AkitaCheckoutService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly analytics: GoogleAnalyticsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.counterSubscription = new Subscription();

    this.payment = new EventEmitter();

    this.counter$ = new Observable<void>();

    this.requestingAgainCounterdownSubject = new BehaviorSubject(0 as any);
    this.requestingAgainCountdown$ =
      this.requestingAgainCounterdownSubject.asObservable();

    this.lessThan$ = this.akitaScreenQuery.selectLessThan();

    this.optFormControl = new UntypedFormControl('', {
      validators: Validators.compose([Validators.required]),
      updateOn: 'change',
    });

    this.sessionPhoneNumber = toSignal(
      this.akitaCheckoutQuery
        .selectSession()
        .pipe(map((session) => session?.phone || null)),
      { initialValue: null }
    );

    this.modifiedPhoneNumber.set(this.sessionPhoneNumber());

    this.focusedSubject = new BehaviorSubject(false as any);
    this.focused$ = this.focusedSubject.asObservable();

    this.processingPaymentSubject = new BehaviorSubject(false as any);
    this.processingPayment$ = this.processingPaymentSubject.asObservable();

    this.requestingOtpAgainSubject = new BehaviorSubject(false as any);
    this.requestingOtpAgain$ = this.requestingOtpAgainSubject.asObservable();
  }

  public ngAfterViewInit(): void {
    this.scrollToTop();
    this.focusInput();
    this.paymentId = this.akitaCheckoutQuery.paymentId;
  }

  private focusInput(): void {
    if (this.akitaRouterQuery.isBrowser) {
      try {
        this.renderer2.selectRootElement('.otpInput', true).focus();
        this.renderer2.selectRootElement('.otpInput', true).select();
        document.addEventListener(
          'click',
          () => {
            this.renderer2.selectRootElement('.otpInput', true).focus();
            this.renderer2.selectRootElement('.otpInput', true).select();
          },
          { once: true }
        );
      } catch (err) {
        /* SSR Not Supported */
      }
    }
  }

  public reStartCounter(): void {
    if (this.requestingAgainCounterdownSubject.value === 0) {
      this.requestingAgainCounterdownSubject.next(60);

      this.counter$ = timer(0, 1000).pipe(
        take(60),
        map(() => {
          if (this.requestingAgainCounterdownSubject.value > 0) {
            this.requestingAgainCounterdownSubject.next(
              this.requestingAgainCounterdownSubject.value - 1
            );
          }
        })
      );

      this.counterSubscription.add(
        this.counter$.subscribe({
          complete: () => {
            this.requestingAgainCounterdownSubject.next(0);
          },
        })
      );
    }
  }

  public onContinuePressed(): boolean {
    if (this.optFormControl.value && this.paymentId) {
      this.subscriptions.add(
        this.akitaCheckoutService
          .sendOtpVerification(this.paymentId, this.optFormControl.value)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: (response) => {
              if (response) {
                const sessionId = this.akitaCheckoutQuery.sessionId;
                const localeFragment = this.akitaRouterQuery.localeFragmentToString;

                const host = getCurrentHost();
                const redirectUrl = `${host}${localeFragment}/checkout/${sessionId}/cko-success`;

                if (redirectUrl) {
                  SentryUtil.addBreadcrumb({
                    category: 'Payment API',
                    message: `User getting redirected to Checkout.com payment page`,
                    level: 'info',
                    type: 'navigation',
                  });
                  try {
                    (window as any).location.href = `${redirectUrl || ''}`;
                  } catch (err) {}
                }
                this.akitaCheckoutService.setIsSubmitting(false);
              }
            },
            error: () => {
              this.zone.run(() => {
                this.processingPaymentSubject.next(false);
                this.akitaCheckoutService.updateCheckoutStep('FAILURE');
              });
            },
          })
      );
      return false;
    }
    return false;
  }

  public onContinuePressedWithLoop(): boolean {
    this.paymentId = this.akitaCheckoutQuery.paymentId;
    this.akitaCheckoutService.setIsSubmitting(true);

    this.akitaCheckoutService.setErrorStcPayment(null);

    if (this.optFormControl.value && this.paymentId) {
      this.subscriptions.add(
        this.akitaCheckoutService
          .sendOtpVerification(this.paymentId, this.optFormControl.value)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: (response) => {
              logSentOTP(
                this.analytics.logEventWrapper,
                this.akitaCheckoutQuery.sessionId || '',
                this.akitaCheckoutQuery.session?.paymentProvider || null,
                this.akitaCheckoutQuery.session?.paymentMethod || null,
                !this.akitaCheckoutQuery.session?.customerId,
                true
              );
              if (!this.akitaCheckoutQuery.isErrorStcPayment && response) {
                if (this.akitaRouterQuery.isBrowser) {
                  setTimeout(() => {
                    this.fetchPaymentStatus();
                  }, PAYMENT_STATUS_FETCH_INTERVAL);
                }
              } else {
                this.reStartCounter();
                this.optFormControl.setValue('');
                this.akitaCheckoutService.updateCheckoutStep('OTP-VERIFICATION');
                this.processingPaymentSubject.next(false);
                this.akitaCheckoutService.setIsSubmitting(false);
                this.phoneNumberConfirmed.set(false);
              }
            },
            error: () => {
              logSentOTP(
                this.analytics.logEventWrapper,
                this.akitaCheckoutQuery.sessionId || '',
                this.akitaCheckoutQuery.session?.paymentProvider || null,
                this.akitaCheckoutQuery.session?.paymentMethod || null,
                !this.akitaCheckoutQuery.session?.customerId,
                true
              );
              this.zone.run(() => {
                this.akitaCheckoutService.setIsSubmitting(false);
                this.processingPaymentSubject.next(false);
                this.akitaCheckoutService.updateCheckoutStep('FAILURE');
              });
            },
          })
      );
      return false;
    }
    return false;
  }

  private fetchPaymentStatus(): void {
    this.zone.runOutsideAngular(() => {
      const sessionId = this.akitaCheckoutQuery.sessionId || null;
      const orderId = this.akitaCheckoutQuery.session?.orderId || null;

      // TODO get order from API
      this.subscriptions.add(
        this.akitaCheckoutService
          .fetchPayment(orderId)
          .pipe(takeUntil(this.destroySubject))
          .subscribe({
            next: (payment: PaymentModel | null) => {
              if (
                payment?.paymentState === 'APPROVED' ||
                payment?.paymentState === 'COMPLETE'
              ) {
                this.navigateToSuccessPage(sessionId || null);
              } else if (
                payment?.paymentState === 'PENDING' ||
                payment?.paymentState === 'WAITING'
              ) {
                this.akitaCheckoutService.setIsSubmitting(false);
                this.requestingOtpAgainSubject.next(true);
              } else {
                this.navigateToPaymentErrorPage(sessionId || null);
              }
            },
            error: (err: unknown) => {
              SentryUtil.addBreadcrumb({
                category: 'Payment Callback',
                message: `Error while fetching payment status from Checkout.com`,
                level: 'error',
                type: 'network',
              });
              SentryUtil.reportException(err, false);
              this.navigateToPaymentErrorPage(sessionId || null);
              this.akitaCheckoutService.setIsSubmitting(false);
            },
          })
      );
    });
  }

  public onCreatePaymentAgain(): void {
    if (
      this.modifiedPhoneNumber()?.display &&
      this.modifiedPhoneNumber()?.display !== this.sessionPhoneNumber()?.display
    ) {
      this.akitaCheckoutService.updateSessionPhoneNumber(this.modifiedPhoneNumber());
    } /*  else {
      this.akitaCheckoutService.updateSessionPhoneNumber(this.sessionPhoneNumber());
    } */

    this.akitaCheckoutService.setIsSubmitting(true);
    this.akitaCheckoutService.setErrorStcPayment(null);

    if (this.akitaCheckoutQuery.sessionId) {
      this.subscriptions.add(
        this.akitaCheckoutService.createPayment().subscribe({
          next: (payment) => {
            if (payment && payment.id && !this.akitaCheckoutQuery.isErrorStcPayment) {
              this.phoneNumberConfirmed.set(true);
              if (this.akitaCheckoutQuery.session?.paymentMethod === 'STC_PAY') {
                logRequestOTP(
                  this.analytics.logEventWrapper,
                  this.akitaCheckoutQuery.sessionId || '',
                  this.akitaCheckoutQuery.session?.paymentProvider || null,
                  this.akitaCheckoutQuery.session?.paymentMethod || null,
                  !this.akitaCheckoutQuery.session?.customerId,
                  false
                );
              }
              this.reStartCounter();
              this.focusInput();
              this.akitaCheckoutService.setPaymentId(payment.id);
              this.akitaCheckoutService.updateCheckoutStep('OTP-VERIFICATION');
              this.akitaCheckoutService.setIsSubmitting(false);
            } else {
              this.akitaCheckoutService.setIsSubmitting(false);
            }
          },
          error: (error: unknown) => {
            const parsedError = parseApiError(error);

            logStcErrorPayment(
              this.analytics.logEventWrapper,
              parsedError.code,
              parsedError.message
            );
            SentryUtil.addBreadcrumb({
              category: 'Order API',
              message: `Error on create/update order for STC_PAY`,
              level: 'error',
              type: 'debug',
            });
            this.akitaCheckoutService.setIsSubmitting(false);
          },
        })
      );
    } else {
      this.akitaCheckoutService.setIsSubmitting(false);
    }
  }

  public onPhoneChange($event: PhoneMetadata | null): void {
    this.modifiedPhoneNumber.set($event || null);
  }

  public onConfirmPhoneNumber(): void {
    this.onCreatePaymentAgain();
  }

  private navigateToPaymentErrorPage(sessionId: string | null): void {
    const host = getCurrentHost();
    const localeFragment = this.akitaRouterQuery.localeFragmentToString;

    const redirectUrl = `${host}${localeFragment}/checkout/${sessionId}/cko-failure`;

    if (redirectUrl) {
      SentryUtil.addBreadcrumb({
        category: 'Payment API',
        message: `User getting redirected to Checkout.com payment page`,
        level: 'info',
        type: 'navigation',
      });
      try {
        (window as any).location.href = `${redirectUrl || ''}`;
      } catch (err) {}
    }
  }

  public navigateToPaymentOverviewPage(): void {
    this.akitaCheckoutService.updateCheckoutStep('REVIEW');
    this.requestingOtpAgainSubject.next(false);
    this.optFormControl.setValue('');
    this.akitaCheckoutService.setIsSubmitting(false);
  }

  private navigateToSuccessPage(sessionId: string | null): void {
    const host = getCurrentHost();
    const localeFragment = this.akitaRouterQuery.localeFragmentToString;
    const redirectUrl = `${host}${localeFragment}/checkout/${sessionId}/cko-success`;

    if (redirectUrl) {
      SentryUtil.addBreadcrumb({
        category: 'Payment API',
        message: `User getting redirected to Checkout.com payment page`,
        level: 'info',
        type: 'navigation',
      });
      try {
        (window as any).location.href = `${redirectUrl || ''}`;
      } catch (err) {}
    }
  }

  public trackByItem(index: number, item?: any): string {
    return item?.productID || `${index}`;
  }

  public getMaskedNumber(phone: PhoneInfo | null): string | null {
    if (!phone) {
      return null;
    }
    return phone?.national.replace(/.(?=.{3,}$)/g, (match) =>
      match === ' ' ? ' ' : 'X'
    );
  }

  public scrollToTop(): void {
    try {
      if (
        this.akitaRouterQuery.isBrowser &&
        window.document &&
        window.document.getElementById
      ) {
        const scrollElement = window.document.getElementById('mainAppScroll');
        if (scrollElement) {
          if (scrollElement.scrollTo) {
            scrollElement.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          } else {
            scrollElement.scrollTop = 0;
          }
        }
      }
    } catch (err) {
      /* SSR Not Supported */
    }
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.counterSubscription) {
      this.counterSubscription.unsubscribe();
    }
  }
}
